/**
 * Генерирует читаемое название грядки на основе номера ряда и позиции
 * Формат: [Буква ряда]-[Позиция с нулями]
 * Примеры: A-0001, B-0017, Z-0042, AA-0001, AB-0123
 * 
 * @param rowNumber - номер ряда (1-based)
 * @param positionNumber - номер позиции в ряду
 * @param bedId - ID грядки (используется как fallback)
 * @returns строка с названием грядки
 */
export function generateBedName(
  rowNumber: number | null | undefined,
  positionNumber: number | null | undefined,
  bedId: string // CN-0012: UUID-only
): string {
  // Если нет номера ряда или позиции, возвращаем простой вариант
  if (rowNumber === null || rowNumber === undefined) {
    return `BED-${bedId}`;
  }

  // Конвертируем номер ряда в буквы (1 -> A, 2 -> B, ..., 27 -> AA, 28 -> AB)
  const rowLetter = numberToLetters(rowNumber);

  // Форматируем позицию с ведущими нулями (по умолчанию 4 цифры)
  const position = positionNumber !== null && positionNumber !== undefined 
    ? String(positionNumber).padStart(4, '0')
    : '0000';

  return `${rowLetter}-${position}`;
}

/**
 * Конвертирует число в буквенное представление
 * 1 -> A, 2 -> B, ..., 26 -> Z, 27 -> AA, 28 -> AB, и т.д.
 */
function numberToLetters(num: number): string {
  let result = '';
  let n = num;

  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }

  return result || 'A';
}

/**
 * Генерирует короткое название грядки (только буква ряда и номер позиции без нулей)
 * Примеры: A1, B17, Z42, AA1, AB123
 */
export function generateShortBedName(
  rowNumber: number | null | undefined,
  positionNumber: number | null | undefined,
  bedId: string // CN-0012: UUID-only
): string {
  if (rowNumber === null || rowNumber === undefined) {
    return `BED${bedId}`;
  }

  const rowLetter = numberToLetters(rowNumber);
  const position = positionNumber !== null && positionNumber !== undefined 
    ? String(positionNumber)
    : '0';

  return `${rowLetter}${position}`;
}
